import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { subscribeUserToPush } from '../../utils/push-notifications';
import SuccessModal from '../support/SuccessModal';
const API_URL = process.env.REACT_APP_API_URL;

const NotificationSettings = () => {
    const { t } = useTranslation();
    const { token } = useAuth();
    
    const [settings, setSettings] = useState({ 
        email_notifications_enabled: false, 
        push_notifications_enabled: false 
    });
    const [browserPermission, setBrowserPermission] = useState(Notification.permission);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const fetchSettings = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/api/dashboard/profile`, { 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Ayarlar alınamadı.');
            const data = await response.json();
            
            // Tarayıcı izni 'granted' değilse, push bildirimleri her zaman kapalıdır.
            const pushEnabled = Notification.permission === 'granted' && data.push_notifications_enabled === 1;

            setSettings({
                email_notifications_enabled: data.email_notifications_enabled === 1,
                push_notifications_enabled: pushEnabled
            });
        } catch (e) { 
            console.error("Ayarlar çekilemedi:", e);
            setError(t('error_fetch_settings'));
        } finally { 
            setLoading(false); 
        }
    }, [token, t]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleToggle = async (key) => {
        const newValue = !settings[key];

        if (key === 'push_notifications_enabled' && newValue === true) {
            // Kullanıcı, Tarayıcı Bildirimlerini AÇMAYA çalışıyor
            if (browserPermission === 'denied') {
                alert(t('alert_notifications_blocked'));
                return; // Hiçbir şey yapma, toggle kapalı kalacak
            }
            
            if (browserPermission === 'default') {
                try {
                    const subscription = await subscribeUserToPush();
                    // Kullanıcı izni verdi
                    await fetch(`${API_URL}/api/profile/subscribe-push`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ subscription })
                    });
                    setBrowserPermission('granted');
                    // İzin alındıktan sonra state'i AÇIK olarak güncelle
                    setSettings(prev => ({ ...prev, push_notifications_enabled: true }));
                     setModalMessage(t('success_subscribed_to_notifications'));
                    setIsSuccessModalOpen(true);
                } catch (error) {
                    // Kullanıcı izni reddetti veya popup'ı kapattı
                    console.error('Abonelik işlemi başarısız veya reddedildi:', error);
                    setBrowserPermission(Notification.permission);
                    // Hiçbir şey yapma, toggle kapalı kalacak
                }
                return; // API çağrısından sonra işlemi burada bitir
            }
        }
        
        // E-posta toggle'ı veya Tarayıcı toggle'ını KAPATMA işlemi için
        // sadece state'i normal bir şekilde güncelle
        setSettings(prev => ({ ...prev, [key]: newValue }));
    };

    const handleSaveChanges = async () => {
        setIsSaving(true); setMessage(''); setError('');
        try {
            const response = await fetch(`${API_URL}/api/profile/notification-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    emailEnabled: settings.email_notifications_enabled ? 1 : 0,
                    pushEnabled: settings.push_notifications_enabled ? 1 : 0
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(t(data.messageKey) || t('error_settings_save'));
            setMessage(t('success_settings_saved'));
        } catch (e) { 
            setError(e.message);
        } finally {
            setIsSaving(false);
        }
    };
    
    if (loading) return <div><i className="fas fa-spinner fa-spin"></i> {t('loading_settings')}</div>;
    
    return (
	<>
        <div className="profile-form-container">
            <h2>{t('notification_settings')}</h2>
            
            <div className="setting-row">
                <div className="setting-info">
                    <strong>{t('email_notifications')}</strong>
                    <p>{t('email_notifications_desc')}</p>
                </div>
                <label className="switch">
                    <input type="checkbox" checked={settings.email_notifications_enabled} onChange={() => handleToggle('email_notifications_enabled')} />
                    <span className="slider round"></span>
                </label>
            </div>
            
            <div className="setting-row">
                <div className="setting-info">
                    <strong>{t('browser_notifications')}</strong>
                    <p>{t('browser_notifications_desc')}</p>
                    {browserPermission === 'denied' && <small className='error-text'>{t('notifications_are_blocked')}</small>}
                </div>
                <label className="switch">
                    <input 
                        type="checkbox" 
                        checked={settings.push_notifications_enabled} 
                        onChange={() => handleToggle('push_notifications_enabled')}
                    />
                    <span className="slider round"></span>
                </label>
            </div>
            
            {error && <p className="auth-error">{error}</p>}
            {message && <p className="auth-success">{message}</p>}

           <button onClick={handleSaveChanges} className="cta-button" disabled={isSaving}>
                {isSaving ? t('saving') : t('save_changes')}
            </button>
        </div>
		
		 <SuccessModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                title={t('success')}
                message={modalMessage}
            />
        </>
    );
};

export default NotificationSettings;